import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Package, Info, StickyNote, RotateCcw } from "lucide-react";
import { domain, domainAccounting } from "../../../../security";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const QtyInput = ({ value, onChange, colorClass }) => (
  <input
    type="number"
    min="0"
    value={value === 0 ? "" : value}
    onChange={onChange}
    placeholder="0"
    className={`w-20 mx-auto block text-center py-1.5 border rounded-lg text-sm font-black outline-none focus:ring-2 transition ${colorClass}`}
  />
);

export default function ItemsReturnedModal({
  returnRecord,
  onClose,
  onSuccess,
  currentUserId,
}) {
  const items = useMemo(() => returnRecord?.items || [], [returnRecord]);
  const [splits, setSplits] = useState({});
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;
    const init = {};
    items.forEach((item) => {
      init[item.productId] = { cashQty: 0, replaceQty: 0 };
    });
    setSplits(init);
  }, [items]);

  const handleChange = (productId, field, rawVal, maxQty) => {
    const val = Math.max(0, parseInt(rawVal) || 0);
    setSplits((prev) => {
      const curr = { ...(prev[productId] || { cashQty: 0, replaceQty: 0 }) };
      curr[field] = val;
      if ((curr.cashQty || 0) + (curr.replaceQty || 0) > maxQty) {
        toast.warning(`Total cannot exceed returned quantity of ${maxQty}.`);
        return prev;
      }
      return { ...prev, [productId]: curr };
    });
  };

  const isBalanced = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((item) => {
      const s = splits[item.productId] || { cashQty: 0, replaceQty: 0 };
      return s.cashQty + s.replaceQty === item.quantityReturned;
    });
  }, [items, splits]);

  const totalRefund = useMemo(
    () =>
      items.reduce((sum, item) => {
        const unitPrice =
          item.quantityReturned > 0
            ? (item.refundAmount || 0) / item.quantityReturned
            : 0;
        return sum + (splits[item.productId]?.cashQty || 0) * unitPrice;
      }, 0),
    [splits, items],
  );

  const handleSubmit = async () => {
    if (!isBalanced) {
      return toast.error(
        "Please assign exactly the quantities returned. No missing items allowed.",
      );
    }

    const payload = {
      UserID: currentUserId,
      DeliveryReturnId: returnRecord.id,
      Note: note,
      Items: items.map((item) => ({
        ProductId: item.productId,
        CashQty: splits[item.productId]?.cashQty || 0,
        ReplaceQty: splits[item.productId]?.replaceQty || 0,
      })),
    };

    let output = null;
    console.log("Payload:", payload);

    setIsLoading(true);
    try {
      const res = await axios.post(`${domain}/api/SalesOrderReturns`, payload);

      output = res.data?.output ?? null

      console.log("Response:", res.data);
      toast.success("Return processed successfully.");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to process return.");
    } finally {
      setIsLoading(false);
      if(output){
        await handlePostToAccounting([output]);
      }
    }
  };

  const handlePostToAccounting = async (output) => {
    try {
      await axios.post(`${domainAccounting}/api/DeliveryReturn/bulk`, output);

      
      toast.success(`Posted to accounting successfully [${output?.length ?? 0}].`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to process return.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex h-full max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="shrink-0 border-b bg-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-xl text-white">
              <Package size={18} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900">
                Process Items Returned
              </h2>
              <p className="text-xs text-slate-500">
                {returnRecord?.returnNumber} —{" "}
                <span className="font-bold text-indigo-600">
                  {returnRecord?.deliveryOrderNumber}
                </span>{" "}
                ·{" "}
                <span className="text-slate-400">
                  {returnRecord?.customerName}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Info Banner */}
        <div className="shrink-0 px-6 py-3 bg-indigo-50 border-b border-indigo-100 flex items-start gap-2">
          <Info size={14} className="text-indigo-500 mt-0.5 shrink-0" />
          <p className="text-xs text-indigo-800">
            You{" "}
            <span className="font-bold text-red-600 underline">
              must assign all returned units
            </span>{" "}
            exactly before you can process. Choose how many are{" "}
            <span className="font-bold">Cash Return</span> and how many are{" "}
            <span className="font-bold">Replace Item</span>.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <Package size={36} className="opacity-30" />
              <p className="text-sm">No items found on this return.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest">
                    <th className="p-3 pl-5 text-slate-500 w-[35%]">Product</th>
                    <th className="p-3 text-center text-slate-500">UOM</th>
                    <th className="p-3 text-center text-slate-700 bg-slate-100/60">
                      Qty Returned
                    </th>
                    <th className="p-3 text-center text-red-600 bg-red-50/60">
                      Cash Return
                    </th>
                    <th className="p-3 text-center text-blue-600 bg-blue-50/60">
                      Replace Item
                    </th>
                    <th className="p-3 text-right pr-5 text-slate-500">
                      Refund Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const s = splits[item.productId] || {};
                    const unitPrice =
                      item.quantityReturned > 0
                        ? (item.refundAmount || 0) / item.quantityReturned
                        : 0;
                    const assigned = (s.cashQty || 0) + (s.replaceQty || 0);
                    const remaining = item.quantityReturned - assigned;
                    const isRowBalanced = remaining === 0;

                    return (
                      <tr
                        key={item.productId}
                        className={`hover:bg-slate-50/60 ${!isRowBalanced ? "bg-amber-50/30" : ""}`}
                      >
                        <td className="p-3 pl-5">
                          <div className="font-bold text-slate-800">
                            {item.productName}
                          </div>
                          {item.brandName && 
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              {item.brandName || ""}
                            </div>
                          }
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {formatCurrency(unitPrice)} / unit · Total value:{" "}
                            <span className="font-bold">
                              {formatCurrency(item.refundAmount)}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-center text-xs text-slate-500 font-medium">
                          {item.salesUomName || "Unit"}
                        </td>
                        <td className="p-3 text-center bg-slate-50/40">
                          <span className="font-black text-slate-700 text-base">
                            {item.quantityReturned}
                          </span>
                          {!isRowBalanced && (
                            <div className="text-[9px] text-amber-500 font-bold mt-0.5 animate-pulse">
                              {remaining} missing
                            </div>
                          )}
                        </td>
                        <td className="p-3 bg-red-50/20">
                          <QtyInput
                            value={s.cashQty || 0}
                            onChange={(e) =>
                              handleChange(
                                item.productId,
                                "cashQty",
                                e.target.value,
                                item.quantityReturned,
                              )
                            }
                            colorClass="border-red-200 text-red-700 focus:ring-red-300 bg-red-50/30"
                          />
                        </td>
                        <td className="p-3 bg-blue-50/20">
                          <QtyInput
                            value={s.replaceQty || 0}
                            onChange={(e) =>
                              handleChange(
                                item.productId,
                                "replaceQty",
                                e.target.value,
                                item.quantityReturned,
                              )
                            }
                            colorClass="border-blue-200 text-blue-700 focus:ring-blue-300 bg-blue-50/30"
                          />
                        </td>
                        <td className="p-3 text-right pr-5 font-black text-red-600">
                          {formatCurrency((s.cashQty || 0) * unitPrice)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td
                      colSpan={3}
                      className="p-3 pl-5 text-xs font-black uppercase text-slate-400"
                    >
                      Totals
                    </td>
                    <td className="p-3 text-center text-red-600 font-black">
                      {Object.values(splits).reduce(
                        (s, v) => s + (v.cashQty || 0),
                        0,
                      )}
                    </td>
                    <td className="p-3 text-center text-blue-600 font-black">
                      {Object.values(splits).reduce(
                        (s, v) => s + (v.replaceQty || 0),
                        0,
                      )}
                    </td>
                    <td className="p-3 pr-5 text-right font-black text-red-600">
                      {formatCurrency(totalRefund)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <label className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-slate-500 mb-2">
              <StickyNote size={12} /> Processing Note
            </label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note for this processing action..."
              className="block w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t bg-slate-50 px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase text-slate-400">
              Cash Refund
            </p>
            <p className="text-xl font-black text-red-600">
              {formatCurrency(totalRefund)}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || !isBalanced}
              className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RotateCcw size={15} /> Process Return
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
